import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/Layout/Layout';
import {
  Card, Button, Badge, SearchInput, Select, Spinner, EmptyState,
  Table, TableHead, TableBody, TableRow, TableCell, Modal, Tabs,
  ImagePreviewModal, isImageAttachment
} from '../../components/UI';
import { callLogsAPI, whatsappTemplatesAPI, filePublicUrl } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePhone, HiOutlinePlay, HiOutlineDownload, HiOutlineUser,
  HiOutlineClipboardList, HiOutlinePaperClip
} from 'react-icons/hi';
import { FaWhatsapp } from 'react-icons/fa';
import { format } from 'date-fns';

const CallLogs = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);
  const [activeTab, setActiveTab] = useState('app'); // 'app' or 'device'
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedLogForWhatsApp, setSelectedLogForWhatsApp] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Fetch users with phones for filter
  const { data: usersWithPhones } = useQuery({
    queryKey: ['usersWithPhones'],
    queryFn: async () => {
      const response = await callLogsAPI.getUsersWithPhones();
      return response.data?.data || { users: [], phoneNumbers: [] };
    },
  });

  // Fetch call logs with filters
  const { data: logsData, isLoading } = useQuery({
    queryKey: ['callLogs', page, statusFilter, userFilter, phoneFilter, search, activeTab],
    queryFn: async () => {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (userFilter) params.userId = userFilter;
      if (phoneFilter) params.phoneNumber = phoneFilter;
      if (search) params.search = search;
      if (activeTab) params.source = activeTab; // 'app' or 'device'
      const response = await callLogsAPI.getAll(params);
      return response.data || {};
    },
  });

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ['callStats', activeTab],
    queryFn: async () => {
      const params = {};
      if (activeTab) params.source = activeTab;
      const response = await callLogsAPI.getStatistics(params);
      return response.data?.data || {};
    },
  });

  // Fetch call counts by source (all calls for tab counts)
  const { data: sourceStats } = useQuery({
    queryKey: ['callSourceStats'],
    queryFn: async () => {
      // Get all calls to show counts for both tabs
      const response = await callLogsAPI.getStatistics({ source: 'all' });
      return response.data?.data || {};
    },
  });

  // Fetch WhatsApp templates
  const { data: templatesData } = useQuery({
    queryKey: ['whatsappTemplates'],
    queryFn: async () => {
      const response = await whatsappTemplatesAPI.getActive();
      return response.data?.data || [];
    },
  });

  const logs = logsData?.data || [];
  const pagination = logsData?.pagination || {};
  const users = usersWithPhones?.users || [];
  const templates = templatesData || [];

  // Get phones for selected user
  const selectedUser = users.find(u => u._id === userFilter);
  const userPhones = selectedUser?.phones?.map(p => ({
    value: p.number,
    label: `${p.number} (${p.label || 'Phone'})`
  })) || [];

  const totalCalls = stats?.total || 0;
  const connectedCalls = stats?.connected || 0;
  const avgDuration = stats?.averageDuration || 0;

  // Get counts by source
  const appCallsCount = sourceStats?.bySource?.app?.total || 0;
  const deviceCallsCount = sourceStats?.bySource?.device?.total || 0;

  // Tabs for call source
  const tabs = [
    { value: 'app', label: 'App Calls', count: appCallsCount },
    { value: 'device', label: 'Device Calls', count: deviceCallsCount },
  ];

  // Show logs filtered by source
  const filteredLogs = logs;

  // Open WhatsApp modal
  const handleOpenWhatsApp = (log) => {
    setSelectedLogForWhatsApp(log);
    setSelectedTemplate(null);
    setShowWhatsAppModal(true);
  };

  // Direct WhatsApp redirect without template
  const handleDirectWhatsApp = (log) => {
    const phoneNumber = log.phoneNumber?.replace(/\D/g, '') || '';
    if (!phoneNumber) {
      toast.error('Invalid phone number');
      return;
    }
    const whatsappUrl = `https://wa.me/${phoneNumber}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp...');
  };

  // Generate WhatsApp link with template and open
  const handleSendWhatsApp = () => {
    if (!selectedLogForWhatsApp || !selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    const phoneNumber = selectedLogForWhatsApp.phoneNumber?.replace(/\D/g, '') || '';
    if (!phoneNumber) {
      toast.error('Invalid phone number');
      return;
    }

    // Replace template variables with lead data
    let message = selectedTemplate.message;
    const leadName = selectedLogForWhatsApp.leadId?.name || selectedLogForWhatsApp.contactName || 'Customer';
    const leadEmail = selectedLogForWhatsApp.leadId?.email || '';

    message = message.replace(/\{\{name\}\}/gi, leadName);
    message = message.replace(/\{\{email\}\}/gi, leadEmail);
    message = message.replace(/\{\{phone\}\}/gi, selectedLogForWhatsApp.phoneNumber || '');

    // Create WhatsApp URL
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    // Open in new tab
    window.open(whatsappUrl, '_blank');
    setShowWhatsAppModal(false);
    toast.success('Redirecting to WhatsApp...');
  };

  // Export call logs to CSV
  const handleExportCallLogs = () => {
    try {
      if (filteredLogs.length === 0) {
        toast.error('No call logs to export');
        return;
      }

      // Create CSV rows
      const csvRows = [];

      // Header section
      csvRows.push('=== CALL LOGS EXPORT ===');
      csvRows.push('');
      csvRows.push(`Export Date: ${new Date().toLocaleString()}`);
      csvRows.push(`Total Calls: ${totalCalls}`);
      csvRows.push(`Connected Calls: ${connectedCalls}`);
      csvRows.push(`Not Connected: ${totalCalls - connectedCalls}`);
      csvRows.push(`Average Duration: ${Math.floor(avgDuration / 60)}m ${avgDuration % 60}s`);
      csvRows.push('');

      // Column headers - comprehensive
      csvRows.push([
        'Phone Number',
        'Lead Name',
        'Lead Email',
        'Call Type',
        'Status',
        'Duration (seconds)',
        'Duration (formatted)',
        'Has Recording',
        'Notes',
        'Date',
        'Time'
      ].join(','));

      // Data rows
      filteredLogs.forEach(log => {
        const durationMin = Math.floor((log.duration || 0) / 60);
        const durationSec = (log.duration || 0) % 60;

        csvRows.push([
          `"${log.phoneNumber || ''}"`,
          `"${log.leadId?.name || 'Unknown'}"`,
          `"${log.leadId?.email || ''}"`,
          `"${log.callType || 'outgoing'}"`,
          `"${log.status || ''}"`,
          `"${log.duration || 0}"`,
          `"${durationMin}m ${durationSec}s"`,
          `"${log.recordingUrl ? 'Yes' : 'No'}"`,
          `"${(log.notes || '').replace(/"/g, '""')}"`,
          `"${log.createdAt ? format(new Date(log.createdAt), 'yyyy-MM-dd') : ''}"`,
          `"${log.createdAt ? format(new Date(log.createdAt), 'HH:mm:ss') : ''}"`
        ].join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `call-logs-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.success(`Exported ${filteredLogs.length} call logs successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export call logs');
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Call Logs</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">View all call recordings and history</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            icon={<HiOutlineDownload className="w-4 h-4" />}
            onClick={handleExportCallLogs}
          >
            <span className="hidden sm:inline">Export</span>
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center">
            <p className="text-xs text-gray-500">Total Calls</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-gray-900">{totalCalls}</p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-green-50">
            <p className="text-xs text-green-600">Connected</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-green-600">{connectedCalls}</p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-blue-50">
            <p className="text-xs text-blue-600">Avg Duration</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-blue-600">
              {Math.floor(avgDuration / 60)}:{(avgDuration % 60).toString().padStart(2, '0')}
            </p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-purple-50">
            <p className="text-xs text-purple-600">Rate</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-purple-600">
              {totalCalls > 0 ? Math.round((connectedCalls / totalCalls) * 100) : 0}%
            </p>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        {/* Filters */}
        <Card padding="p-3 sm:p-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <div className="flex-1">
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search by phone or lead..."
                />
              </div>
              <div className="flex gap-2">
                <Select
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'connected', label: 'Connected' },
                    { value: 'not_connected', label: 'Not Connected' },
                    { value: 'busy', label: 'Busy' },
                    { value: 'no_answer', label: 'No Answer' },
                  ]}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="flex-1 min-w-[100px]"
                />
                <Select
                  options={[
                    { value: '', label: 'All Users' },
                    ...users.map(u => ({
                      value: u._id,
                      label: u.name
                    }))
                  ]}
                  value={userFilter}
                  onChange={(e) => {
                    setUserFilter(e.target.value);
                    setPhoneFilter(''); // Reset phone filter when user changes
                  }}
                  className="flex-1 min-w-[100px]"
                />
              </div>
            </div>
            {userFilter && userPhones.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Select
                  options={[
                    { value: '', label: 'All Phones' },
                    ...userPhones
                  ]}
                  value={phoneFilter}
                  onChange={(e) => setPhoneFilter(e.target.value)}
                  className="sm:w-48"
                />
              </div>
            )}
          </div>
        </Card>

        {/* Call Logs List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlinePhone className="w-8 h-8 text-gray-400" />}
              title="No call logs found"
              description="Call logs will appear here once calls are made"
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableCell header className="min-w-[100px]">Phone</TableCell>
                    <TableCell header className="min-w-[120px]">Contact/Lead</TableCell>
                    <TableCell header className="min-w-[100px]">User</TableCell>
                    <TableCell header className="min-w-[80px]">Type</TableCell>
                    <TableCell header className="min-w-[90px]">Status</TableCell>
                    <TableCell header className="min-w-[60px]">Duration</TableCell>
                    <TableCell header className="min-w-[100px]">Date</TableCell>
                    <TableCell header className="min-w-[120px]">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLogs.map((log) => (
                    <TableRow key={log._id}>
                      <TableCell>
                        <span className="font-medium text-gray-900 whitespace-nowrap">{log.phoneNumber}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 truncate max-w-[150px] block">
                          {log.leadId?.name || log.contactName || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <HiOutlineUser className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-600 text-sm truncate max-w-[100px]">
                            {log.userId?.firstName} {log.userId?.lastName}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            log.callType === 'incoming' ? 'info' :
                              log.callType === 'missed' ? 'danger' : 'primary'
                          }
                          size="sm"
                        >
                          {log.callType || 'outgoing'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={log.status === 'connected' ? 'success' : 'danger'}
                          size="sm"
                        >
                          {log.status?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 whitespace-nowrap">
                          {log.duration
                            ? `${Math.floor(log.duration / 60)}:${(log.duration % 60).toString().padStart(2, '0')}`
                            : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-500 text-sm whitespace-nowrap">
                          {log.createdAt ? format(new Date(log.createdAt), 'MMM d, h:mm a') : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 flex-nowrap">
                          {/* Recording Button */}
                          {/* {log.recordingUrl ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5"
                              icon={<HiOutlinePlay className="w-4 h-4 text-primary-600" />}
                              onClick={() => setSelectedLog(log)}
                              title="Play Recording"
                            />
                          ) : (
                            <span className="w-8 h-8 flex items-center justify-center text-gray-300">
                              <HiOutlinePlay className="w-4 h-4" />
                            </span>
                          )} */}
                          {/* WhatsApp Direct Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            icon={<FaWhatsapp className="w-4 h-4 text-green-600" />}
                            onClick={() => handleDirectWhatsApp(log)}
                            title="Open WhatsApp Chat"
                          />
                          {/* WhatsApp with Template Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            icon={<HiOutlineClipboardList className="w-4 h-4 text-green-700" />}
                            onClick={() => handleOpenWhatsApp(log)}
                            title="Send via Template"
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filteredLogs.map((log) => (
                <div key={log._id} className="p-3 sm:p-4 hover:bg-gray-50 transition-colors">
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">{log.phoneNumber}</p>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">
                        {log.leadId?.name || log.contactName || 'Unknown Lead'}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        <HiOutlineUser className="w-3 h-3 inline mr-1" />
                        {log.userId?.firstName} {log.userId?.lastName}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <Badge
                        variant={
                          log.callType === 'incoming' ? 'info' :
                            log.callType === 'missed' ? 'danger' : 'primary'
                        }
                        size="sm"
                      >
                        {log.callType || 'outgoing'}
                      </Badge>
                      <Badge
                        variant={log.status === 'connected' ? 'success' : 'danger'}
                        size="sm"
                      >
                        {log.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>

                  {/* Info Row */}
                  <div className="flex items-center justify-between text-xs sm:text-sm mb-3">
                    <span className="text-gray-400">
                      {log.createdAt ? format(new Date(log.createdAt), 'MMM d, h:mm a') : '-'}
                    </span>
                    <span className="text-gray-500 font-medium">
                      {log.duration
                        ? `${Math.floor(log.duration / 60)}:${(log.duration % 60).toString().padStart(2, '0')}`
                        : '-'}
                    </span>
                  </div>

                  {/* Actions Row */}
                  <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100">
                    {log.recordingUrl ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2 flex-1"
                        icon={<HiOutlinePlay className="w-4 h-4 text-primary-600" />}
                        onClick={() => setSelectedLog(log)}
                      >
                        <span className="text-xs">Play</span>
                      </Button>
                    ) : (
                      <span className="p-2 text-gray-300 flex-1 text-center">
                        <HiOutlinePlay className="w-4 h-4 inline" />
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2 flex-1"
                      icon={<FaWhatsapp className="w-4 h-4 text-green-600" />}
                      onClick={() => handleDirectWhatsApp(log)}
                    >
                      <span className="text-xs">Chat</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2 flex-1"
                      icon={<HiOutlineClipboardList className="w-4 h-4 text-green-700" />}
                      onClick={() => handleOpenWhatsApp(log)}
                    >
                      <span className="text-xs">Template</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between p-3 sm:p-4 border-t border-gray-100">
                <span className="text-xs sm:text-sm text-gray-500">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="flex gap-1.5 sm:gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Recording Modal */}
        <Modal
          isOpen={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          title="Call Recording"
          size="md"
        >
          {selectedLog && (
            <div className="space-y-3 sm:space-y-4">
              <div className="bg-gray-50 p-3 sm:p-4 rounded-xl">
                <div className="flex items-start sm:items-center justify-between gap-2 mb-3 sm:mb-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{selectedLog.phoneNumber}</p>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">
                      {selectedLog.leadId?.name || 'Unknown Lead'}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 sm:mt-1">
                      User: {selectedLog.userId?.firstName} {selectedLog.userId?.lastName}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <Badge
                      variant={
                        selectedLog.callType === 'incoming' ? 'info' :
                          selectedLog.callType === 'missed' ? 'danger' : 'primary'
                      }
                      size="sm"
                    >
                      {selectedLog.callType || 'outgoing'}
                    </Badge>
                    <Badge variant={selectedLog.status === 'connected' ? 'success' : 'danger'} size="sm">
                      {selectedLog.status}
                    </Badge>
                  </div>
                </div>
                <div className="text-xs sm:text-sm text-gray-500 mb-3">
                  {selectedLog.createdAt
                    ? format(new Date(selectedLog.createdAt), 'MMM d, yyyy h:mm a')
                    : '-'}
                  {' • '}
                  {selectedLog.duration
                    ? `${Math.floor(selectedLog.duration / 60)}:${(selectedLog.duration % 60).toString().padStart(2, '0')}`
                    : '0:00'}
                </div>
                <audio
                  controls
                  className="w-full"
                  src={selectedLog.recordingUrl}
                >
                  Your browser does not support the audio element.
                </audio>
              </div>

              {selectedLog.attachments && selectedLog.attachments.length > 0 && (
                <div className="bg-gray-50 p-3 sm:p-4 rounded-xl">
                  <p className="text-xs sm:text-sm font-medium text-gray-500 mb-2">
                    Attachments ({selectedLog.attachments.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedLog.attachments.map((att, aIndex) => {
                      const isImg = isImageAttachment(att);
                      const url = filePublicUrl(att.url);
                      const label = att.originalName || att.fileName || 'Attachment';
                      const cls = "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors max-w-[240px] cursor-pointer";
                      const inner = (
                        <>
                          <HiOutlinePaperClip className="w-4 h-4 flex-shrink-0" />
                          <span className="text-xs font-medium truncate">{label}</span>
                        </>
                      );
                      return isImg ? (
                        <button
                          key={att._id || `${att.fileName}-${aIndex}`}
                          type="button"
                          title={label}
                          className={cls}
                          onClick={() => setPreviewImage(url)}
                        >
                          {inner}
                        </button>
                      ) : (
                        <a
                          key={att._id || `${att.fileName}-${aIndex}`}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={label}
                          className={cls}
                        >
                          {inner}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  icon={<HiOutlineDownload className="w-4 h-4" />}
                  onClick={() => {
                    window.open(selectedLog.recordingUrl, '_blank');
                    toast.success('Download started');
                  }}
                >
                  Download
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* WhatsApp Template Modal */}
        <Modal
          isOpen={showWhatsAppModal}
          onClose={() => setShowWhatsAppModal(false)}
          title="Send WhatsApp Message"
          size="md"
        >
          {selectedLogForWhatsApp && (
            <div className="space-y-3 sm:space-y-4">
              {/* Contact Info */}
              <div className="bg-gray-50 p-3 sm:p-4 rounded-xl">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <FaWhatsapp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{selectedLogForWhatsApp.phoneNumber}</p>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">
                      {selectedLogForWhatsApp.leadId?.name || selectedLogForWhatsApp.contactName || 'Unknown Contact'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <button
                  onClick={() => handleDirectWhatsApp(selectedLogForWhatsApp)}
                  className="flex flex-col items-center gap-1.5 sm:gap-2 p-3 sm:p-4 rounded-xl border-2 border-green-200 bg-green-50 hover:bg-green-100 transition-colors"
                >
                  <FaWhatsapp className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                  <span className="font-medium text-green-700 text-sm sm:text-base">Direct Chat</span>
                  <span className="text-xs text-green-600 hidden sm:block">Open WhatsApp directly</span>
                </button>
                <div className="flex flex-col items-center gap-1.5 sm:gap-2 p-3 sm:p-4 rounded-xl border-2 border-primary-200 bg-primary-50">
                  <HiOutlineClipboardList className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />
                  <span className="font-medium text-primary-700 text-sm sm:text-base">Use Template</span>
                  <span className="text-xs text-primary-600 hidden sm:block">Select a message template</span>
                </div>
              </div>

              {/* Template Selection */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                  Select Template
                </label>
                {templates.length === 0 ? (
                  <p className="text-xs sm:text-sm text-gray-500">No templates available</p>
                ) : (
                  <div className="space-y-2 max-h-48 sm:max-h-60 overflow-y-auto">
                    {templates.map((template) => (
                      <div
                        key={template._id}
                        onClick={() => setSelectedTemplate(template)}
                        className={`p-2.5 sm:p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedTemplate?._id === template._id
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-900 text-sm">{template.name}</span>
                          <Badge variant="default" size="sm">{template.category}</Badge>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{template.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Preview */}
              {selectedTemplate && (
                <div className="bg-green-50 p-3 sm:p-4 rounded-xl border border-green-200">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                    <FaWhatsapp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600" />
                    <span className="text-xs sm:text-sm font-medium text-green-700">Message Preview</span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedTemplate.message
                      .replace(/\{\{name\}\}/gi, selectedLogForWhatsApp.leadId?.name || selectedLogForWhatsApp.contactName || 'Customer')
                      .replace(/\{\{email\}\}/gi, selectedLogForWhatsApp.leadId?.email || '')
                      .replace(/\{\{phone\}\}/gi, selectedLogForWhatsApp.phoneNumber || '')}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 sm:gap-3">
                <Button variant="ghost" size="sm" onClick={() => setShowWhatsAppModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<FaWhatsapp className="w-4 h-4" />}
                  onClick={handleSendWhatsApp}
                  disabled={!selectedTemplate}
                >
                  Send
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>

      {/* Attachment image preview lightbox */}
      <ImagePreviewModal src={previewImage} onClose={() => setPreviewImage(null)} />
    </Layout>
  );
};

export default CallLogs;